<template>
	<div class="checkout">
		<div id="briqpay-overlay"></div>
		<customerTypeSelector
			v-model="isCorporateCustomer"
			class="customer-type"
			:isLoggedIn="isLoggedIn"
		/>
		<checkoutCart
			ref="cartDiv"
			:vouchers="vouchers"
			:cartOverride="predefinedCart"
			@voucherUpdate="updateVouchers"
			@moveToPaymentMethods="stepHandler(3, null)"
			:orderTotalSum.sync="orderTotalSum"
			:cartSum.sync="cartSum"
			:selectedPaymentMethodId="selectedPaymentMethodId"
		/>
		<div class="steps-wrap" ref="checkoutDiv">
			<!-- Step 1 -->
			<stepOne
				id="stepScroll1"
				:isActive.sync="steps.one"
				:isLoggedIn="isLoggedIn"
				:predefinedCart="predefinedCart"
				@stepSelection="handleStepSelection"
			/>

			<!-- Step 2 -->
			<deliveryMethod
				id="stepScroll2"
				:activeStep="activeStep"
				:stockMap="stockMap"
				:testConstraint="testConstraint"
				:updateOrderFlags="updateOrderFlags"
				:selectedShippingMethodId.sync="selectedShippingMethodId"
				:selectedShippingOption.sync="selectedShippingOption"
				:selectedStoreId.sync="selectedStoreId"
				:isCorporateCustomer="isCorporateCustomer"
				:shippingSiteInfo.sync="shippingSiteInfo"
				:predefinedCart="predefinedCart"
				:isActive.sync="steps.two"
				:orderTotalSum="orderTotalSum"
				:cartSum="cartSum"
				:weight="weight"
				:volume="volume"
				:highestPackageSizeId.sync="highestPackageSizeId"
				@stepSelection="handleStepSelection"
				@stepUpdated="stepHandler"
				@storeReservationUpdated="setIsStoreReservation"
				@stockAvailability="setStockAvailability"
			/>

			<!-- Step 3 -->
			<orderDetails
				id="stepScroll3"
				:isActive.sync="steps.three"
				:activeStep="activeStep"
				:testConstraint="testConstraint"
				:selectedShippingMethodId="selectedShippingMethodId"
				:isCorporateCustomer="isCorporateCustomer"
				:isStoreReservation="isStoreReservation"
				:vouchers="vouchers"
				:cart="cart"
				:pendingAmount="pendingAmount"
				:customerInfoData.sync="customerInfoData"
				:pickupAndPayInStore.sync="pickupAndPayInStore"
				:highestPackageSizeId="highestPackageSizeId"
				@stepSelection="handleStepSelection"
				@finished="stepHandler"
			/>

			<!-- Step 4 -->
			<paymentMethods
				id="stepScroll4"
				:activeStep="activeStep"
				:isActive.sync="steps.four"
				:isCorporateCustomer="isCorporateCustomer"
				:selectedShippingMethodId="selectedShippingMethodId"
				:selectedShippingOption="selectedShippingOption"
				:isStoreReservation="isStoreReservation"
				:selectedStoreId="selectedStoreId"
				:pickupAndPayInStore="pickupAndPayInStore"
				:customerInfoData="customerInfoData"
				:vouchers="vouchers"
				:paymentLink="paymentLink"
				:pendingAmount="pendingAmount"
				:cart="cart"
				@showBriqpayOverlay="showOverlay"
				@hideBriqpayOverlay="hideOverlay"
				@stepSelection="handleStepSelection"
				@finished="stepHandler"
			/>
		</div>
	</div>
</template>

<script>
import { cloneDeep, throttle } from 'lodash';
import alert from 'alert';
import bus from 'eventbus';
import xpress from 'xpress';
import store from 'datastore';
import { scrollTo, scrollToY } from 'utils';
import { mapState } from 'vuex';
import businessVariables from 'businessVariables';
import localSessionStorage from 'localSessionStorage';

import metaHandler from 'metaHandler';
import { onAnalyticsReady } from 'vue-analytics';
import { trackImpression, trackCheckoutStep, selectCheckoutOption, trackCheckoutOption, trackCheckoutError, formatGtmProducts } from 'gtm';

import checkoutCart from './checkout-cart';
import paymentMethods from './payment-methods';
import deliveryMethod from './delivery-method';
import customerTypeSelector from './customer-type-selector';

import stepOne from './step-one';

import stepHeader from './step-header';
import deliveryMethodSelector from './delivery-method-selector';
import orderDetails from './order-details';

export default {
	store,
	data() {
		return {
			isCorporateCustomer: false,
			vouchers: [],
			selectedShippingMethodId: null,
			selectedShippingOption: null,
			selectedPaymentMethodId: null,
			constraints: [],
			orderFlags: [],
			resursPaymentOptions: [],
			shippingSiteInfo: {},
			steps: {
				one: true,
				two: false,
				three: false,
				four: false,
			},
			activeStep: 1,
			stepTracker: {
				'1': {
					'id': 1,
					'name': 'Välj Leveranssätt',
					'value': true,
				},
				'2': {
					'id': 2,
					'name': 'Välj Fraktsätt',
					'value': false,
				},
				'3': {
					'id': 3,
					'name': 'Välj Betalsätt',
					'value': false,
				},
			},
			selectedStoreId: null,
			isStoreReservation: false,
			partialDelivery: false,
			cartHeight: 500,
			pendingAmount: 0,
			stockAvailability: null,
			orderTotalSum: null,
			cartSum: null,
			trackingData: {
				1: null,
				2: null,
				3: null,
			},
			highestPackageSizeId: 0,

			// För orderlänkar
			predefinedCart: null,
			paymentLink: null,
			customerInfoData: {},
			pickupAndPayInStore: false,
		};
	},
	components: {
		checkoutCart,
		paymentMethods,
		deliveryMethod,
		customerTypeSelector,

		stepOne,

		stepHeader,
		deliveryMethodSelector,
		orderDetails,
	},
	beforeRouteEnter (to, frm, next) {
		// @TODO ensure that user info is loaded before proceeding
		if (to.params.paymentLink) {
			waja.get('order/link/' + to.params.paymentLink)
				.on('success', res => {
					waja.multiRequest([
						waja.get('order-constraint'),
						waja.get('order/flags/' + to.params.paymentLink),
					], responses => next(vm => {
						vm.predefinedCart = res.data.cart;
						vm.paymentLink = to.params.paymentLink;
						vm.constraints = responses[0].data.orderConstraints;
						vm.orderFlags = responses[1].data.flags;
					}), responses => { // Något anrop gav felkod
						if (responses[1].status !== 200) {
							alert('Betallänken är inte giltig!');
							next('/');
							return;
						}
						alert('Något gick fel vid laddning av kassan!'); // Bör inte hända
						next('/');
					});
				})
				.on('404', res => {
					alert('Betallänken är inte giltig!');
					next('/');
				})
				.go();
			return;
		}
		store.dispatch('cart/refresh', {
			onSuccess: data => {
				if (! (data && data.cart && data.cart.rows.length > 0)) {
					alert('Du kan inte gå till kassan med en tom kundvagn!');
					next('/');
				}
				waja.get('order-constraint')
					.on('success', res => {
						waja.get('order/flags')
							.on('success', res2 => {
								next(vm => {
									// vm.corrections = data.corrections;
									vm.orderFlags = res2.data.flags;
									vm.constraints = res.data.orderConstraints;
								});
							}).on('404', res => {
								alert('Du kan inte gå till kassan med en tom kundvagn!');
								next('/');
							}).go();
					}).go(); // @TODO chaina ihop dessa på något snyggare sätt....
			}, onError: e => {
				alert('Du kan inte gå till kassan med en tom kundvagn!'); // @TODO inte najs att ha en massa såna här..
				next('/');
			},
		});
	},
	mounted () {
		if (this.$route.query.error) {
			trackCheckoutError(this.activeStep, this.$route.query.error);
		}
		bus.$on('isCorporateCustomer', this.setCustomerType);
		bus.$on('stepUpdated', this.stepHandler);
		bus.$on('selectedPayment', this.setPayment);
		bus.$on('total-amount', this.setPendingAmount);

		/**
		 * Checking if page load is triggered by return from briqpay
		 * if yes -> set data and send user to step four
		 */
		let checkoutData = localSessionStorage.get('checkoutData');
		if (checkoutData) {
			checkoutData = JSON.parse(checkoutData);
		}

		if (checkoutData && checkoutData?.returningFromBriqpay) {
			this.isCorporateCustomer = checkoutData.isCorporateCustomer;

			this.stepTracker[1].value = true;
			this.stepTracker[2].value = true;
			this.stepTracker[3].value = true;
			if (! checkoutData.isStoreReservation) {
				this.selectedShippingMethodId = checkoutData.selectedShippingMethodId;
			}

			this.activeStep = 4;
			this.steps.one = false;
			this.steps.two = false;
			this.steps.three = false;
			this.steps.four = true;

		} else {
			this.isCorporateCustomer = this.user.isCorporateCustomer;
		}

		onAnalyticsReady().then(() => {
			if (window.ga) {
				window.ga(tracker => {
					this.analyticsClientId = tracker.get('clientId');
				});
			}
		});

		metaHandler.set('robots', 'noindex, follow');

		this.trackGAImpression();

		let orderSum = 0;
		const productIds = [];
		for (const row of this.cart.rows) {
			orderSum += row.unitPrice * row.quantity;
			productIds.push(row.product.id);
		}

		const el = document.createElement('script');
		el.src = 'https://api.briqpay.com/briq.min.js';
		el.id = 'briq-sdk';
		el.type = 'text/javascript';
		el.async = true;
		const d = (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]);
		d.appendChild(el);
	},
	updated() {
		this.getCartHeight();
	},
	beforeDestroy() {
		bus.$off('isCorporateCustomer', this.setCustomerType);
		bus.$off('selectedPayment', this.setPayment);
		bus.$off('total-amount', this.setPendingAmount);
	},
	methods: {
		showOverlay() {
			const overlay = document.getElementById('briqpay-overlay');
			if (overlay) {
				console.log('showOverlay triggered');
				overlay.style.display = 'block';
				overlay.style.top = '0';
				overlay.style.left = '0';
				overlay.style.width = '100%';
				overlay.style.height = '100%';
				overlay.style.position = 'absolute';
				overlay.style.background = 'white';
				overlay.style.opacity = '0.8';
				overlay.style.zIndex = '9998';
			}
		},
		hideOverlay() {
			const overlay = document.getElementById('briqpay-overlay');
			if (overlay) {
				console.log('hideOverlay triggered');
				overlay.style.display = 'none';
				overlay.removeAttribute('style');
			}
		},
		setPendingAmount (amount) {
			if (this.pendingAmount != amount) {
				this.pendingAmount = amount;
			}
		},
		setPayment (payment) {
			this.selectedPaymentMethodId = payment;
		},
		updateVouchers (updatedVouchers) {
			this.vouchers = updatedVouchers;
		},
		stepHandler (step, option) {
			this.activeStep = step;
			console.log('calling stephanlder', step, option);
			if (step === 1 && option === null) {
				this.steps.one = true;
				this.steps.two = false;
				this.steps.three = false;
				this.steps.four = false;
			} else if (step === 2 && option === null) {
				this.steps.one = false;
				this.steps.two = true;
				this.steps.three = false;
				this.steps.four = false;
			} else if (step === 3 && option === null) {
				this.steps.one = false;
				this.steps.two = false;
				this.steps.three = true;
				this.steps.four = false;
			} else if (step === 4 && option === null) {
				this.steps.one = false;
				this.steps.two = false;
				this.steps.three = false;
				this.steps.four = true;
			} else if (this.steps.one) {
				this.steps.one = false;
				this.steps.two = true;
				this.steps.three = false;
				this.steps.four = false;
			} else if (this.steps.two) {
				this.steps.one = false;
				this.steps.two = false;
				this.steps.three = true;
				this.steps.four = false;
			} else if (this.steps.three) {
				this.steps.one = false;
				this.steps.two = false;
				this.steps.three = false;
				this.steps.four = true;
			}

			if (step !== 4) {
				const stepElement = document.getElementById('stepScroll' + step);
				scrollTo(stepElement.offsetTop);
			}
		},
		setCustomerType (val) {
			this.selectedStoreId = null;
			this.selectedPaymentMethodId = null;
			this.isStoreReservation = false;
			this.isCorporateCustomer = val;

			this.$nextTick(() => {
				this.stepHandler(1, null);
			});
		},
		setStockAvailability (val) {
			this.stockAvailability = val;
		},
		testConstraint (key, value) {
			const constr = this.enabledConstraints;

			if (constr.deny[key] && constr.deny[key].includes(value)) {
				return false;
			}
			if (constr.restrictTo[key] && ! constr.restrictTo[key].includes(value)) {
				return false;
			}
			return true;
		},
		hasFlag (flag) {
			return this.orderFlags.includes(flag);
		},
		setIsStoreReservation (isStoreReservation) {
			this.isStoreReservation = isStoreReservation;
		},
		updateOrderFlags () {
			let url = 'order/flags';
			if (this.$route.params.paymentLink) {
				url += '/' + this.$route.params.paymentLink;
			}
			waja.get(url)
				.on('success', res => {
					this.orderFlags = res.data.flags;
				}).on('404', res => {
					next('/');
				}).go();
		},
		getCartHeight () {
			if (! this.$refs.cartDiv) {
				this.cartHeight = 500;
			} else {
				this.cartHeight = this.$refs.cartDiv.$el.clientHeight;
			}
		},
		handleMissingStep (stepNumber) {
			if (stepNumber === 3) {
				this.setPayment(null);
			}
			this.stepHandler(stepNumber, null);
		},
		handleStepSelection (val) {
			Object.assign(this.trackingData, val);
		},
		trackGAImpression () {
			/* Send event to GA */
			const gtmProducts = formatGtmProducts(this.cart.rows);

			trackImpression(gtmProducts, 'checkout', { coupon: true, event: 'eec_checkout' });
		}
	},
	computed: {
		// showKcoIframe () {
		// 	return this.selectedPaymentMethodId === 20 && ! this.reloadingKco && ! this.isCorporateCustomer; // klarna checkout
		// },
		cart () {
			if (this.predefinedCart) {
				return this.predefinedCart;
			}
			return this.$store.state.cart;
		},
		volume() {
			let cartVolume = 0;
			let insufficientData = false;
			this.cart.rows.forEach (row => {
				if (row.product.isPhysical) {
					if (row.product.packageVolume) {
						cartVolume += row.product.packageVolume * row.quantity;
					} else {
						insufficientData = true;
					}
				}
			});
			if (insufficientData) {
				return null;
			} else {
				return cartVolume;
			}
		},
		weight() {
			let cartWeight = 0;
			let insufficientData = false;
			this.cart.rows.forEach (row => {
				if (row.product.isPhysical) {
					if (row.product.packageWeight) {
						cartWeight += row.product.packageWeight * row.quantity;
					} else {
						insufficientData = true;
					}
				}
			});
			if (insufficientData) {
				return null;
			} else {
				return cartWeight;
			}
		},
		totalPrice () {
			return this.pendingAmount;
		},
		stockMap () {
			const map = {};
			for (const row of this.cart.rows) {
				map[row.product.id] = row.product.stock;
			}
			return map;
		},
		enabledConstraints () {
			const enabled = {
				deny: {},
				restrictTo: {},
				allowVouchers: true,
				errors: [],
			};

			for (const i in this.constraints) {
				const constraint = this.constraints[i];
				const trueResult = ! constraint.condition || xpress.run(constraint.condition, this.constraintHandler);
				if (trueResult) {
					for (const prop in constraint.constraint) {
						if (prop === 'allowVouchers') {
							enabled.allowVouchers = constraint.constraint[prop];
							continue;
						}
						const constr = constraint.constraint[prop];
						if (constr.deny) {
							const value = constr.deny;
							const previous = enabled['deny'][ prop ] || [];
							enabled['deny'][ prop ] = previous.concat(Array.isArray(value) ? value : [value]);
						}
						if (constr.restrictTo) {
							let value = constr.restrictTo;
							if (! Array.isArray(value)) {
								value = [value];
							} else {
								value = cloneDeep(value); // annars blir det strul med reaktiviteten eftersom vue lägger på osynliga properties
							}
							if (! enabled.restrictTo[ prop ]) {
								enabled.restrictTo[ prop ] = value;
							} else {
								for (let j = 0; j < enabled.restrictTo[ prop ].length; j++) {
									if (! value.includes(enabled.restrictTo[ prop ][j])) {
										enabled.restrictTo[ prop ].splice(j--, 1);
									}
								}
							}
						}

						if (prop === 'invalidateOrder') {
							enabled.errors.push(constraint.constraint[prop]);
						}
					}
				}
			}
			return enabled;
		},
		constraintHandler () {
			//OBS synka denna med andra ställen!
			return {
				get: key => {
					switch (key) {
						case 'shippingMethod':
							return this.selectedShippingMethodId;
						case 'paymentMethod':
							return this.selectedPaymentMethodId;
						case 'totalPrice':
							return this.totalPrice;
						case 'pendingAmount':
							return this.pendingAmount;
						case 'maximumShippingClassOrder':
							return this.maximumShippingClassOrder;
						case 'totalPriceExclShipping':
							return this.totalPrice - this.shippingPrice;
						case 'isCorporateCustomer':
							return this.isCorporateCustomer;
						case 'isLimitedByShippingSite':
							return this.shippingSiteInfo && this.shippingSiteInfo?.isLimitedByShippingSite;
						case 'minimumPurchaseForFreeShipping':
							return businessVariables.minimumPurchaseForFreeShipping[this.cart.currency];
						case 'orderVolume':
							return this.volume;
						case 'orderWeight':
							return this.weight;
					}
				},
				call: (key, args) => {
					switch (key) {
						case 'max':
						case 'min':
						case 'abs':
							return Math[key.toLowerCase()](...args);
						case 'flag':
							return this.hasFlag(args[0]);
						case 'countRowsWhere':
							let count = 0;
							for (const row of this.cart.rows) {
								count += xpress.run(args[0], {
									get: rowKey => {
										if (rowKey === 'packageSize') {
											return row.product.packageSizeId;
										} else if (rowKey === 'length1') {
											return row.product.packageMeasurements[0];
										} else if (rowKey === 'length2') {
											return row.product.packageMeasurements[1];
										} else if (rowKey === 'length3') {
											return row.product.packageMeasurements[2];
										}
										// else if (rowKey === 'limitedByShippingSite') {
										// return (this.shippingSiteInfo.rows?.length ? this.shippingSiteInfo.rows[row.id].isLimitedByShippingSite : 1);
										// }
									},
								}) ? row.quantity : 0;
							}
							return count;
					}
				},
			};
		},
		throttledUpdateOrderFlags () {
			return throttle(this.updateOrderFlags, 500);
		},
		totalCartSum () {
			let price = 0;
			this.cart.rows.forEach(function (row) {
				price += row.unitPrice * row.quantity
			})
			return price;
		},
		...mapState({
			user: state => state.user,
			isLoggedIn: 'user/isLoggedIn',
		}),
	},
	watch: {
		isCorporateCustomer (newValue, oldValue) {
			localSessionStorage.set('checkout:isCorporateCustomer', newValue);
		},
		cart: {
			handler (to) {
				// @TODO hämta inte detta när kundvagn ändras pga att köp slutförs
				// @TODO triggar flera gånger per kundvagnsändring
				// bus.$emit('update:reloadingKco', true);
				if (! this.cart.rows.length) {
					this.$router.push(this.$router.route({
						name: 'index',
					}));
					return;
				} else {
					this.$store.dispatch('cart/onceClean').then(cart => {
						// bus.$emit('reloadKlarna');
						this.throttledUpdateOrderFlags();
					});
				}
			},
			deep: true,
		},
		$route (to, frm) {
			if (to.query.error) {
				trackCheckoutError(this.activeStep, to.query.error);
			}
		},
		activeStep (to, frm) {
			if (to !== 1 && to !== frm) {
				trackCheckoutStep(to);
			}

			if (to !== frm && frm !== 4) {
				trackCheckoutOption(frm, this.trackingData[frm] ?? 'not available');
			}

			if (this.stepTracker[to]) {
				this.stepTracker[to].value = true;
			}

			if (to === 1 && frm > 1) {
				this.selectedShippingMethodId = null;
				this.selectedShippingOption = null;
				this.selectedStoreId = null;
				this.stepTracker[2].value = false;
				this.stepTracker[3].value = false;
				this.trackGAImpression();
			}
		},
		selectedShippingMethodId () {
			if (this.stepTracker[2].value === false) {
				this.selectedShippingMethodId = null;
			}
		},
		isCorporateCustomer (to) {
			selectCheckoutOption(this.activeStep, to ? 'Företagskund' : 'Privatperson', 'select_customer_type');
		}
	},
};
</script>
